import { createSelector } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getKey } from 'store/createKeyedDataAsyncSlice';
import { loadOrderAsyncAction } from 'store/order/batchedDispatches';
import { currentOrderSlice } from 'store/order/currentOrder/currentOrderSlice';
import { useAppSelector, useAppDispatch } from 'store/store';
import { RootStore } from 'store/types';
import { assertDefined } from 'utilities/Assertions';

export const selectCurrentOrder = createSelector(
  [
    (state: RootStore) => state.order.orderInformationSlice,
    (state: RootStore) => state.order.currentOrderSlice.orderId,
  ],
  (orderSlice, orderId) => getKey(orderId, orderSlice)
);

/** Use me when you know the current order is loaded, usually inside of a page. */
export const useSelectCurrentOrder = () => {
  const { data, fetchStatus, fetchError } = useAppSelector(selectCurrentOrder);
  assertDefined(data, 'Using useSelectCurrentOrder when the order is not guaranteed to be there, is unsupported.');
  return { data, fetchStatus, fetchError };
};

export const useSelectCurrentFeesVersion = () => {
  const { data } = useSelectCurrentOrder();
  return data.fees_version;
};

export const useSelectCurrentOrderId = () => {
  return useAppSelector((state) => state.order.currentOrderSlice.orderId);
};

export const useSelectAndLoadCurrentOrder = (orderId?: string) => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (orderId) {
      dispatch(loadOrderAsyncAction(orderId));
      dispatch(currentOrderSlice.actions.setCurrentOrderId(orderId));
    }
    return () => {
      dispatch(currentOrderSlice.actions.clearCurrentOrder());
    };
  }, [orderId, dispatch]);

  return useSelector(selectCurrentOrder);
};
